import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';

export type Density = 'compact' | 'normal' | 'relaxed';

const DENSITY_PADDING: Record<Density, string> = {
  compact: 'px-3 py-1.5',
  normal: 'px-4 py-2.5',
  relaxed: 'px-6 py-4',
};

/** Scrolling container: the table scrolls horizontally, the page never does. */
export function TableContainer({ className = '', ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`overflow-x-auto rounded-xl border border-border bg-surface ${className}`}
      {...rest}
    />
  );
}

export function Table({
  density = 'compact',
  className = '',
  children,
  ...rest
}: HTMLAttributes<HTMLTableElement> & { density?: Density }) {
  return (
    <table data-density={density} className={`w-full text-sm ${className}`} {...rest}>
      {children}
    </table>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return <thead className="sticky top-0 z-10 bg-bg-s1">{children}</thead>;
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-border">{children}</tbody>;
}

export function TR({
  className = '',
  interactive,
  ...rest
}: HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean }) {
  return (
    <tr
      className={`${interactive ? 'hover:bg-bg-subtle cursor-pointer' : ''} ${className}`}
      {...rest}
    />
  );
}

export function TH({
  density = 'compact',
  numeric,
  className = '',
  children,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { density?: Density; numeric?: boolean }) {
  return (
    <th
      scope="col"
      className={[
        DENSITY_PADDING[density],
        'text-xs font-medium uppercase tracking-wide text-text-secondary',
        numeric ? 'text-right' : 'text-left',
        className,
      ].join(' ')}
      {...rest}
    >
      {children}
    </th>
  );
}

export function TD({
  density = 'compact',
  numeric,
  className = '',
  children,
  ...rest
}: TdHTMLAttributes<HTMLTableCellElement> & { density?: Density; numeric?: boolean }) {
  return (
    <td
      className={[
        DENSITY_PADDING[density],
        'text-text-primary',
        numeric ? 'text-right tabular-nums' : 'text-left',
        className,
      ].join(' ')}
      {...rest}
    >
      {children}
    </td>
  );
}
